import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  PrescriberInService,
  transformPrescriberForUI,
} from '../../pages/PrescriberProfilePage/utils'
import { setPrescriber } from '../../pages/NewScriptPage/ScriptForm/PrescriberSlice'
import { Prescriber } from '../../types'
import { Permission } from '../../types/permission'
import ApiClient from '../../utils/api/ApiClient'

/** Raw wire shape of `POST /auth/ehr-launch/bootstrap`. */
export interface EhrBootstrapResponse {
  userId: string
  prescriber: PrescriberInService
  permissions: Permission[]
  launchInstanceId: string
  patientId?: string
}

/**
 * Bootstrap result as it reaches the store. `prescriber` is already in the UI
 * shape here, so every consumer - including the reducer that persists it - sees
 * a single, consistent shape.
 */
export interface EhrBootstrapPayload extends Omit<EhrBootstrapResponse, 'prescriber'> {
  prescriber: Prescriber | null
}

interface EhrLaunchState {
  launchInstanceId?: string
  hasError: boolean
}

export const EhrLaunchClient = {
  Bootstrap: createAsyncThunk<EhrBootstrapPayload, void>(
    'ehrLaunch/bootstrap',
    async (_, thunkAPI) => {
      const { data } = await ApiClient.post<EhrBootstrapResponse>('/auth/ehr-launch/bootstrap')
      // `qualifications` is a MySQL simple-json column, so the wire value is a
      // string[]. Every reader of state.prescriber and localStorage['prescriber']
      // expects the UI shape, so normalise here - once - and hand the normalised
      // value to both the store and the persist step below. The response type says
      // the prescriber is always present, but the transform normalises a missing or
      // unusable one to null rather than throwing, which is the same state as no
      // prescriber: a guard here would be dead code that hides that contract.
      const prescriber = transformPrescriberForUI(data.prescriber)
      thunkAPI.dispatch(setPrescriber(prescriber))
      return { ...data, prescriber }
    }
  ),
}

const initialState: EhrLaunchState = {
  hasError: false,
}

const ehrLaunchSlice = createSlice({
  name: 'ehrLaunch',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(EhrLaunchClient.Bootstrap.fulfilled, (state, { payload }) => {
        state.launchInstanceId = payload.launchInstanceId
        state.hasError = false
        localStorage.setItem('user_id', payload.userId)
        // Same shape setPrescriber already wrote - never the raw API payload. The key
        // holds a usable prescriber or is absent, never the string `"null"`.
        if (payload.prescriber) {
          localStorage.setItem('prescriber', JSON.stringify(payload.prescriber))
        }
        localStorage.setItem('permissions', JSON.stringify(payload.permissions))
      })
      .addCase(EhrLaunchClient.Bootstrap.rejected, (state) => {
        state.hasError = true
      })
  },
})

export default ehrLaunchSlice.reducer
